import { auth0DomainCheckAtom } from "@/modules/login/auth0/auth0Config.ts";
import { auth0ProviderAtom } from "@/modules/login/auth0/auth0Provider.ts";
import { isStorybook, isTestEnvironment } from "@/utils/environment.ts";
import { atom } from "jotai/vanilla";

export interface RedirectParams {
    /** NB: Only allow specifying a relative path to prevent open redirect attacks */
    redirectPath?: string;
}

export interface SignupParams extends RedirectParams {
    username: string;
    password: string;
}

export interface SignupResult {
    email: string | undefined;
    emailVerified: boolean;
}

export interface LoginParams extends RedirectParams {
    username: string;
    password: string;
}

export interface ForgotPasswordParams extends RedirectParams {
    email: string;
}

export interface ChangePasswordParams extends ForgotPasswordParams {}

export type SocialLoginId = "google-oauth2";

export interface SocialLoginParams extends RedirectParams {
    connectionId: SocialLoginId;
}

export interface LogoutParams extends RedirectParams {}

export interface AuthProvider {
    signup(params: SignupParams): Promise<SignupResult>;

    login(params: LoginParams): Promise<void>;
    socialLogin(params: SocialLoginParams): Promise<void>;
    logout(params: LogoutParams): Promise<void>;

    forgotPassword(params: ForgotPasswordParams): Promise<unknown>;
    changePassword(params: ChangePasswordParams): Promise<string>;

    isFake?: true;
}

function logAlert(message: string, ...args: unknown[]) {
    console.log(message, ...args);
    if (!isTestEnvironment) {
        alert(message);
    }
}

const fakeAuthProvider: AuthProvider = {
    isFake: true,

    /* eslint-disable @typescript-eslint/require-await */
    async signup(p) {
        logAlert("Fake signup", p);
        return { email: "user@example.com", emailVerified: false };
    },
    async login(p) {
        logAlert("Fake login", p);
    },
    async socialLogin(p) {
        logAlert("Fake google login", p);
    },
    async forgotPassword(p) {
        logAlert("Fake forgot password", p);
    },
    async changePassword(p) {
        logAlert("Fake change password", p);
        return "Fake password changed";
    },
    async logout(p) {
        console.log("Fake logout", p);
    },
    /* eslint-enable @typescript-eslint/require-await */
};

export const authProviderAtom = atom<AuthProvider>((get) => {
    return !isStorybook && get(auth0DomainCheckAtom).isValid ? get(auth0ProviderAtom) : fakeAuthProvider;
});
